import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const ABC_NLP_IMG =
  "https://customer-assets.emergentagent.com/job_wellness-tracker-pro-31/artifacts/13dmu88q_kwaliteitvanleven-768x539.png";

// Per-day weights, identical to backend _baseline_from_onboarding
const WEIGHTS = {
  perfect: 0.049,
  very_good: 0.016,
  good: 0.0,
  okayish: -0.016,
  bad: -0.032,
  very_bad: -0.049,
};

const clamp = (x) => Math.max(1, Math.min(99, x));

function baselineFor(scores) {
  if (!scores) return 50;
  let total = 50;
  for (const k of Object.keys(WEIGHTS)) {
    const v = parseInt(scores[k], 10) || 0;
    total += v * WEIGHTS[k];
  }
  return clamp(total);
}

/**
 * Final onboarding screen.
 *
 * Computes the user's average baseline (across emotions/past/future/inner_self_talk)
 * using the same formula that seeds the QoL probability chart. Routes the user
 * toward coaching (≤ 37%), either (37–50%), or training (> 50%) — with a CTA.
 */
export default function OnboardingConclusion({ answers, clientType, onFinish }) {
  const { t } = useT();
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const subjects = ["emotions", "past", "future", "inner_self_talk"];
  const baselines = subjects.map((s) => baselineFor(answers?.[s]));
  const average = baselines.reduce((a, b) => a + b, 0) / baselines.length;
  const avg = Math.round(average * 100) / 100;

  let band; // "coaching" | "either" | "training"
  if (avg <= 37) band = "coaching";
  else if (avg <= 50) band = "either";
  else band = "training";

  const canRequestCoaching = clientType === "prospect" || clientType === "trainee";

  const requestCoaching = async () => {
    setRequesting(true);
    try {
      await api.post("/qol/me/request-coaching", {
        reason: "onboarding_recommendation",
        note: `Average quality-of-life baseline at onboarding: ${avg}%. Recommendation band: ${band}.`,
      });
      setRequested(true);
      toast.success(t("conclusion.toast.notified"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("bt_test.toast.could_not_send"));
    } finally {
      setRequesting(false);
    }
  };

  const finishAndGo = () => {
    // For training band the user should land on /me/upcoming-events after the
    // onboarding wraps up; otherwise /me/dashboard (their new home).
    const target = band === "training" ? "/me/upcoming-events" : "/me/dashboard";
    onFinish?.(target);
  };

  return (
    <div data-testid="onboarding-conclusion">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188] mb-4">
        {t("conclusion.overline")}
      </p>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D1A] font-light leading-[1.1]">
        {t("conclusion.headline_a")}{" "}
        <span className="font-semibold">{t("conclusion.headline_b")}</span> {t("conclusion.headline_c")}{" "}
        <span
          className="font-semibold"
          data-testid="conclusion-average"
          style={{
            color:
              band === "coaching" ? "#9C6A53" : band === "either" ? "#B37A5F" : "#4A5D4E",
          }}
        >
          {avg}%
        </span>
      </h1>
      <p className="mt-4 text-[#5C635A] leading-relaxed max-w-2xl">
        {t("conclusion.body")}
      </p>

      {/* The graphic */}
      <div
        className="mt-10 bg-white border border-[#E5E2DC] rounded-2xl p-4 sm:p-6"
        data-testid="conclusion-graphic"
      >
        <img
          src={ABC_NLP_IMG}
          alt="ABC-NLP kwaliteit van leven — coaching, coaching of training, training bands"
          className="w-full h-auto rounded-xl"
        />
      </div>

      {/* Individual subject baselines */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="conclusion-baselines">
        {[
          [t("conclusion.subject.feelings"), baselines[0]],
          [t("conclusion.subject.past"), baselines[1]],
          [t("conclusion.subject.future"), baselines[2]],
          [t("conclusion.subject.inner_self_talk"), baselines[3]],
        ].map(([label, val]) => (
          <div
            key={label}
            className="bg-white border border-[#E5E2DC] rounded-xl p-4"
          >
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold">{label}</p>
            <p className="font-heading text-2xl text-[#1A1D1A] font-semibold mt-1">
              {Math.round(val * 100) / 100}%
            </p>
          </div>
        ))}
      </div>

      {/* Recommendation block */}
      <div
        className="mt-10 bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
        data-testid={`conclusion-recommendation-${band}`}
      >
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-white flex items-center justify-center shrink-0 border border-[#E5E2DC]">
            <Sparkles
              strokeWidth={1.5}
              size={22}
              className={
                band === "coaching"
                  ? "text-[#9C6A53]"
                  : band === "either"
                  ? "text-[#B37A5F]"
                  : "text-[#4A5D4E]"
              }
            />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-2xl text-[#1A1D1A] tracking-tight font-semibold">
              {band === "coaching"
                ? t("conclusion.band.coaching.title")
                : band === "either"
                ? t("conclusion.band.either.title")
                : t("conclusion.band.training.title")}
            </h3>
            <p className="text-sm sm:text-base text-[#5C635A] mt-2 leading-relaxed">
              {band === "coaching" && t("conclusion.band.coaching.body", { avg })}
              {band === "either" && t("conclusion.band.either.body", { avg })}
              {band === "training" && t("conclusion.band.training.body", { avg })}
            </p>
          </div>
        </div>

        {(band === "coaching" || band === "either") && (
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              onClick={requestCoaching}
              disabled={!canRequestCoaching || requesting || requested}
              data-testid="conclusion-request-coaching-button"
              className="h-12 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white px-6"
            >
              {requested ? (
                <>
                  <CheckCircle2 strokeWidth={1.5} size={18} className="mr-2" />
                  {t("conclusion.coach_notified_pending")}
                </>
              ) : (
                <>
                  <Sparkles strokeWidth={1.5} size={18} className="mr-2" />
                  {requesting ? t("conclusion.sending") : t("conclusion.request_coaching")}
                </>
              )}
            </Button>
          </div>
        )}
        {!canRequestCoaching && (band === "coaching" || band === "either") && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold mt-4">
            {t("conclusion.eligible_note")}
          </p>
        )}
      </div>

      {/* Finish */}
      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold max-w-md">
          {t("conclusion.continue_note")}
        </p>
        <Button
          type="button"
          onClick={() => finishAndGo()}
          data-testid="conclusion-continue-to-qol-button"
          className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white px-6"
        >
          {t("onboarding.continue")}
          <ArrowRight strokeWidth={1.5} size={18} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
